import { useTranslation } from "react-i18next";

interface Feature {
  id: number;
  key: string;
  icon: string;
}

const Features = () => {
  const { t } = useTranslation();

  const features: Feature[] = [
    { id: 1, key: "speed", icon: "speed" },
    { id: 2, key: "reliability", icon: "reliability" },
    { id: 3, key: "innovation", icon: "innovation" },
    { id: 4, key: "support", icon: "support" },
  ];

  const getFeatureIcon = (iconType: string, colorClass: string) => {
    switch (iconType) {
      case "speed":
        return (
          <svg
            className={`w-12 h-12 ${colorClass}`}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path
              d="M13 2L3 14h8l-1 8 10-12h-8l1-8z"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="currentColor"
              opacity="0.2"
            />
            <path
              d="M13 2L3 14h8l-1 8 10-12h-8l1-8z"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
            <circle
              cx="13"
              cy="2"
              r="1.5"
              fill="currentColor"
              className="animate-pulse"
            />
            <circle
              cx="11"
              cy="14"
              r="1.5"
              fill="currentColor"
              className="animate-pulse"
              style={{ animationDelay: "0.2s" }}
            />
            <circle
              cx="13"
              cy="22"
              r="1.5"
              fill="currentColor"
              className="animate-pulse"
              style={{ animationDelay: "0.4s" }}
            />
          </svg>
        );
      case "reliability":
        return (
          <svg
            className={`w-12 h-12 ${colorClass}`}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="12" cy="12" r="3" fill="currentColor" />
            <circle cx="12" cy="12" r="8" opacity="0.3" strokeWidth="1.5" />
            <circle cx="12" cy="4" r="1.5" fill="currentColor" />
            <circle cx="20" cy="12" r="1.5" fill="currentColor" />
            <circle cx="12" cy="20" r="1.5" fill="currentColor" />
            <circle cx="4" cy="12" r="1.5" fill="currentColor" />
            <line x1="12" y1="9" x2="12" y2="5.5" strokeWidth="1.5" />
            <line x1="15" y1="12" x2="18.5" y2="12" strokeWidth="1.5" />
            <line x1="12" y1="15" x2="12" y2="18.5" strokeWidth="1.5" />
            <line x1="9" y1="12" x2="5.5" y2="12" strokeWidth="1.5" />
          </svg>
        );
      case "innovation":
        return (
          <svg
            className={`w-12 h-12 ${colorClass}`}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path
              d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707"
              strokeLinecap="round"
            />
            <circle cx="12" cy="12" r="4" />
            <path d="M12 8v8M8 12h8" strokeLinecap="round" opacity="0.6" />
          </svg>
        );
      case "support":
        return (
          <svg
            className={`w-12 h-12 ${colorClass}`}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="12" cy="12" r="2.5" fill="currentColor" />
            <circle cx="12" cy="5" r="2" strokeWidth="1.5" />
            <circle cx="19" cy="12" r="2" strokeWidth="1.5" />
            <circle cx="12" cy="19" r="2" strokeWidth="1.5" />
            <circle cx="5" cy="12" r="2" strokeWidth="1.5" />
            <line x1="12" y1="9.5" x2="12" y2="7" strokeWidth="1.5" />
            <line x1="14.5" y1="12" x2="17" y2="12" strokeWidth="1.5" />
            <line x1="12" y1="14.5" x2="12" y2="17" strokeWidth="1.5" />
            <line x1="9.5" y1="12" x2="7" y2="12" strokeWidth="1.5" />
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <section id="features" className="py-24 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-8 relative z-10">
        <div className="text-center mb-16 animate-fade-in">
          <h2 className="text-4xl md:text-6xl font-bold mb-6 gradient-text">
            {t("features.title")}
          </h2>
          <p className="text-xl md:text-2xl text-neuro-cyan/80">
            {t("features.subtitle")}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((feature, index) => (
            <div
              key={feature.id}
              className="group relative glass-neuro p-8 rounded-2xl hover:bg-dark-900/80 transition-all duration-500 text-center overflow-hidden animate-slide-up border border-neuro-cyan/10 hover:border-neuro-cyan/30 cursor-pointer"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Neuro Glow Background on Hover */}
              <div
                className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 ${
                  index === 0
                    ? "neuro-glow-cyan"
                    : index === 1
                    ? "neuro-glow-purple"
                    : index === 2
                    ? "neuro-glow-azure"
                    : "neuro-glow-cyan"
                }`}
              ></div>

              {/* Hexagon Icon - Neuro Colors */}
              <div className="relative z-10 mb-6 flex justify-center">
                {/* Outer glow effect */}
                <div
                  className={`absolute ${
                    index === 0
                      ? "bg-neuro-cyan/30"
                      : index === 1
                      ? "bg-neuro-purple/30"
                      : index === 2
                      ? "bg-neuro-azure/30"
                      : "bg-neuro-cyan/30"
                  } blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500`}
                  style={{ width: "120px", height: "138px" }}
                ></div>

                <div
                  className={`hexagon-large relative ${
                    index === 0
                      ? "bg-gradient-to-br from-neuro-cyan/30 to-neuro-cyan/10"
                      : index === 1
                      ? "bg-gradient-to-br from-neuro-purple/30 to-neuro-purple/10"
                      : index === 2
                      ? "bg-gradient-to-br from-neuro-azure/30 to-neuro-azure/10"
                      : "bg-gradient-to-br from-neuro-cyan/30 to-neuro-cyan/10"
                  } border-2 ${
                    index === 0
                      ? "border-neuro-cyan/40 group-hover:border-neuro-cyan"
                      : index === 1
                      ? "border-neuro-purple/40 group-hover:border-neuro-purple"
                      : index === 2
                      ? "border-neuro-azure/40 group-hover:border-neuro-azure"
                      : "border-neuro-cyan/40 group-hover:border-neuro-cyan"
                  } transition-all duration-500 animate-hex-glow group-hover:scale-110`}
                  style={{
                    width: "80px",
                    height: "92px",
                    boxShadow:
                      index === 0
                        ? "0 0 20px rgba(0, 245, 212, 0.3), inset 0 0 20px rgba(0, 245, 212, 0.1)"
                        : index === 1
                        ? "0 0 20px rgba(138, 46, 255, 0.3), inset 0 0 20px rgba(138, 46, 255, 0.1)"
                        : index === 2
                        ? "0 0 20px rgba(79, 225, 255, 0.3), inset 0 0 20px rgba(79, 225, 255, 0.1)"
                        : "0 0 20px rgba(0, 245, 212, 0.3), inset 0 0 20px rgba(0, 245, 212, 0.1)",
                  }}
                >
                  {/* Inner shadow for depth */}
                  <div
                    className="absolute inset-0 hexagon-large opacity-50"
                    style={{
                      background:
                        index === 0
                          ? "linear-gradient(135deg, rgba(0, 245, 212, 0.2) 0%, transparent 50%, rgba(0, 0, 0, 0.3) 100%)"
                          : index === 1
                          ? "linear-gradient(135deg, rgba(138, 46, 255, 0.2) 0%, transparent 50%, rgba(0, 0, 0, 0.3) 100%)"
                          : index === 2
                          ? "linear-gradient(135deg, rgba(79, 225, 255, 0.2) 0%, transparent 50%, rgba(0, 0, 0, 0.3) 100%)"
                          : "linear-gradient(135deg, rgba(0, 245, 212, 0.2) 0%, transparent 50%, rgba(0, 0, 0, 0.3) 100%)",
                    }}
                  ></div>

                  <div className="absolute inset-0 flex items-center justify-center transform group-hover:scale-110 transition-transform duration-500 drop-shadow-lg">
                    {getFeatureIcon(
                      feature.icon,
                      index === 0
                        ? "text-neuro-cyan drop-shadow-[0_0_8px_rgba(0,245,212,0.8)]"
                        : index === 1
                        ? "text-neuro-purple drop-shadow-[0_0_8px_rgba(138,46,255,0.8)]"
                        : index === 2
                        ? "text-neuro-azure drop-shadow-[0_0_8px_rgba(79,225,255,0.8)]"
                        : "text-neuro-cyan drop-shadow-[0_0_8px_rgba(0,245,212,0.8)]"
                    )}
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="relative z-10">
                <h3
                  className={`text-xl font-bold mb-4 transition-colors ${
                    index === 0
                      ? "text-neuro-cyan group-hover:text-white"
                      : index === 1
                      ? "text-neuro-purple group-hover:text-white"
                      : index === 2
                      ? "text-neuro-azure group-hover:text-white"
                      : "text-neuro-cyan group-hover:text-white"
                  }`}
                >
                  {t(`features.items.${feature.key}.title`)}
                </h3>
                <p className="text-gray-300 leading-relaxed group-hover:text-white transition-colors">
                  {t(`features.items.${feature.key}.description`)}
                </p>
              </div>

              {/* Neuro Connection Lines */}
              <div className="absolute bottom-0 left-0 right-0 h-0.5 neuro-line opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Features;
